import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { handleQuerySuccess } from '../../../../hooks/useQuriesSuccess';
import { apiRequest } from './../../../../Services/Common/ApiRequest';
import Global from '../../../../Services/Common/Global';
import useSearchMutaion from '../../../../hooks/apis/useSearchMutaion';
import useDeleteMutation from '../../../../hooks/apis/useDeleteMutaion';
import useSaveMutation from '../../../../hooks/apis/useSaveMutaion';
import { mapParamsBySchema, mapRowsBySchema } from '../../../../util';
import { SUB_BASIC_03_Schema } from './SUB_BASIC_03_Schema';

/**
 * 도급사업 안전보건 협의체 회의록(SUB_BASIC_03) 관련 React Query 훅 모음
 * - 기본 조회(useQuery): useSearch1 (GetSubBasicMst03R1 - 회의록 조회)
 * - Mutation 조회:
 *   - useSearch2 (GetSubBasicMst03R2 - 안건 조회)
 *   - useSearch3 (GetSubBasicMst03R3 - 회의록 상세 조회)
 *   - useSearch5, useSearch6
 * - 저장: useSave1, useSave2, useSave3, useSave4
 * - 삭제: useDelete1, useDelete2, useDelete3
 *
 * 만약 MUTATION 훅에서 공통 응답 처리 외에 추가 성공 콜백이 필요하면
 * successOptions 파라미터로 onSuccess 콜백 함수를 넘겨줄 수 있다.
 */

export const useSearch1 = (options = {}) => {
  const { onSuccess } = options;
  const params = mapParamsBySchema(SUB_BASIC_03_Schema.mapper.search1);

  const query = useQuery({
    queryKey: ['SubcontractorManagement', 'getSubBasicMst03R1', params],
    queryFn: () =>
      apiRequest(
        'post',
        Global.urls.SubcontractorManagement.getSubBasicMst03R1,
        params
      ),

    enabled: true, // 자동 실행 여부
  });

  useEffect(() => {
    if (!query.isSuccess) return;

    handleQuerySuccess(query.data, {
      onSuccess: async (rows) => {
        await onSuccess?.(rows);
      },
    });
  }, [query.isSuccess, query.dataUpdatedAt]);

  return query;
};

export const useSearch2 = (successOptions) => {
  return useSearchMutaion((context) => {
    const encryptedParams = mapParamsBySchema(
      SUB_BASIC_03_Schema.mapper.search2,
      context
    );
    return apiRequest(
      'post',
      Global.urls.SubcontractorManagement.getSubBasicMst03R2,
      encryptedParams
    );
  }, successOptions);
};

export const useSearch3 = (successOptions) => {
  return useSearchMutaion(
    (context) => {
      const encryptedParams = mapParamsBySchema(
        SUB_BASIC_03_Schema.mapper.search3,
        context
      );
      return apiRequest(
        'post',
        Global.urls.SubcontractorManagement.getSubBasicMst03R3,
        encryptedParams
      );
    },
    {
      ...successOptions,
      onSuccess: (rows) => {
        // useSearch3는 전체 배열 전달 (회의 기본정보 + 참석자 정보)
        const dataArray = Array.isArray(rows) ? rows : [rows];
        successOptions?.onSuccess?.(dataArray);
      },
    }
  );
};

export const useSearch5 = (successOptions) => {
  return useSearchMutaion((context) => {
    const encryptedParams = mapParamsBySchema(
      SUB_BASIC_03_Schema.mapper.search5,
      context
    );
    return apiRequest(
      'post',
      Global.urls.Member.getMemBasicR1,
      encryptedParams
    );
  }, successOptions);
};

export const useSearch6 = (successOptions) => {
  return useSearchMutaion((context) => {
    const encryptedParams = mapParamsBySchema(
      SUB_BASIC_03_Schema.mapper.search6,
      context
    );
    return apiRequest(
      'post',
      Global.urls.SubcontractorManagement.getSubBasic01R1,
      encryptedParams
    );
  }, successOptions);
};

export const useSave1 = (options = {}) => {
  return useSaveMutation(async (rows, context) => {
    const payload = mapRowsBySchema(rows, SUB_BASIC_03_Schema.mapper.save1, {
      context,
    });
    return apiRequest(
      'post1',
      Global.urls.SubcontractorManagement.saveSubBasic03U1,
      payload
    );
  }, options);
};

export const useSave2 = (options = {}) => {
  return useSaveMutation(async (rows, context) => {
    const payload = mapRowsBySchema(rows, SUB_BASIC_03_Schema.mapper.save2, {
      context,
    });

    return apiRequest(
      'post1',
      Global.urls.SubcontractorManagement.saveSubBasic03U2,
      payload
    );
  }, options);
};

export const useSave3 = (options = {}) => {
  return useSaveMutation(async (rows, context) => {
    const payload = mapRowsBySchema(rows, SUB_BASIC_03_Schema.mapper.save3, {
      context,
    });

    return apiRequest(
      'post1',
      Global.urls.SubcontractorManagement.saveSubBasic03U3,
      payload
    );
  }, options);
};

export const useSave4 = (options = {}) => {
  return useSaveMutation(async (rows) => {
    // 서버는 배열을 받고 배열로 응답함
    const payloadArray = mapRowsBySchema(
      rows,
      SUB_BASIC_03_Schema.mapper.save4,
      {
        userInfo: options.userInfo,
      }
    );

    return apiRequest(
      'post1',
      Global.urls.SubcontractorManagement.saveSubBasic03U4,
      payloadArray
    );
  }, options);
};

export const useDelete1 = (options = {}) => {
  return useDeleteMutation(async (rows) => {
    // 서버는 List<...>를 받으므로 배열(JSON array)로 전송
    const payload = mapRowsBySchema(rows, SUB_BASIC_03_Schema.mapper.delete1);
    return apiRequest(
      'post1',
      Global.urls.SubcontractorManagement.deleteSubBasic03D1,
      payload
    );
  }, options);
};

export const useDelete2 = (options = {}) => {
  return useDeleteMutation(async (rows) => {
    // 서버는 List<...>를 받으므로 배열(JSON array)로 전송
    // delete2: 회의록 결과 삭제 (DeleteSubBasic03D2) - Seq 기반
    const payload = mapRowsBySchema(rows, SUB_BASIC_03_Schema.mapper.delete2);
    return apiRequest(
      'post1',
      Global.urls.SubcontractorManagement.deleteSubBasic03D2,
      payload
    );
  }, options);
};

export const useDelete3 = (options = {}) => {
  return useDeleteMutation(async (rows) => {
    // 서버는 List<...>를 받으므로 배열(JSON array)로 전송
    // delete3: 회의록 참석자 삭제 (DeleteSubBasic03D3) - Gubun 기반
    const payload = mapRowsBySchema(rows, SUB_BASIC_03_Schema.mapper.delete3);
    return apiRequest(
      'post1',
      Global.urls.SubcontractorManagement.deleteSubBasic03D3,
      payload
    );
  }, options);
};
